import type { FormEvent, ReactNode } from 'react'
import type { TelescopeNight } from '@/lib/server/telescope'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { buttonClass } from '@/lib/ui'
import { formatKey } from '@/lib/time'
import { formatPrice } from '@/constants/tickets'
import { Sheet } from '@/components/generals/sheet'

import {
    Choice,
    Notice,
    Stepper,
    TextArea,
    TextField
} from '@/components/generals/form'

interface NightBookingProps {
    night: TelescopeNight | null
    cardPayments: boolean
    onClose: () => void
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/** The short form for a place on the roof: group size, an optional note, and contact details. */
export const NightBooking = ({ night, cardPayments, onClose }: NightBookingProps): ReactNode => {
    const router = useRouter()
    const [places, setPlaces] = useState(2)
    const [members, setMembers] = useState(0)
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [note, setNote] = useState('')
    const [payment, setPayment] = useState<'card' | 'on_arrival'>(cardPayments ? 'card' : 'on_arrival')
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [message, setMessage] = useState('')
    const [sending, setSending] = useState(false)

    useEffect(() => {
        if (!night) return

        setPlaces(value => Math.min(Math.max(1, value), Math.max(1, Math.min(6, night.remaining))))
        setMessage('')
        setErrors({})
    }, [night])

    useEffect(() => {
        setMembers(value => Math.min(value, places))
    }, [places])

    const max = night ? Math.max(1, Math.min(6, night.remaining)) : 6
    const total = night ? (places - members) * night.pricePence + members * night.memberPricePence : 0

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!night) return

        const nextErrors: Record<string, string> = {}

        if (name.trim().length < 2) nextErrors.name = 'Please tell us your name.'
        if (!EMAIL_PATTERN.test(email.trim())) nextErrors.email = 'Please check your email address.'

        setErrors(nextErrors)

        if (Object.keys(nextErrors).length) return

        setSending(true)
        setMessage('')

        try {
            const response = await fetch('/api/bookings/telescope', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ nightId: night.id, places, memberPlaces: members, name, email, phone, note, payment })
            })
            const data = await response.json().catch(() => null) as { redirectUrl?: string, message?: string, errors?: Record<string, string> } | null

            if (!response.ok || !data?.redirectUrl) {
                if (data?.errors) setErrors(data.errors)
                setMessage(data?.message ?? 'Something went wrong. Nothing has been charged.')
                setSending(false)
                return
            }

            if (data.redirectUrl.startsWith('http')) window.location.assign(data.redirectUrl)
            else void router.push(data.redirectUrl)
        }
        catch {
            setMessage('We could not reach the booking system. Nothing has been charged. Please try again.')
            setSending(false)
        }
    }

    return (
        <Sheet
            open={night !== null}
            onClose={onClose}
            eyebrow={night ? `${formatKey(night.nightDate, 'long')} · ${night.startsLocal} to ${night.endsLocal}` : undefined}
            title={night?.title ?? ''}
        >
            {night && (
                <form onSubmit={handleSubmit} noValidate>
                    <p className='text-star-2'>
                        {`Small groups of up to ${night.capacity} on the roof deck with ${night.astronomer}. ${night.remaining} place${night.remaining === 1 ? '' : 's'} left.`}
                    </p>

                    <div className='mt-8 flex items-center justify-between gap-4 border-y border-hair py-5'>
                        <div>
                            <p className='text-lg text-star'>
                                Group size
                            </p>
                            <p className='text-sm text-star-3'>
                                {`${formatPrice(night.pricePence)} each`}
                            </p>
                        </div>
                        <Stepper label='People' value={places} onChange={setPlaces} min={1} max={max} />
                    </div>

                    <div className='flex items-center justify-between gap-4 border-b border-hair py-5'>
                        <div>
                            <p className='text-lg text-star'>
                                Members in the group
                            </p>
                            <p className='text-sm text-star-3'>
                                {`${formatPrice(night.memberPricePence)} each, card shown on the night`}
                            </p>
                        </div>
                        <Stepper label='Members' value={members} onChange={setMembers} min={0} max={places} />
                    </div>

                    <div className='mt-8 space-y-8'>
                        <TextField id='night-name' label='Full name' value={name} onChange={setName} autoComplete='name' required error={errors.name} />
                        <TextField id='night-email' label='Email' type='email' inputMode='email' value={email} onChange={setEmail} autoComplete='email' required error={errors.email} hint='We will email you if the weather changes the plan.' />
                        <TextField id='night-phone' label='Phone' type='tel' inputMode='tel' value={phone} onChange={setPhone} autoComplete='tel' hint='For a text on the day if clouds roll in.' />
                        <TextArea
                            id='night-note'
                            label='Accessibility or anything we should know'
                            value={note}
                            onChange={setNote}
                            rows={3}
                            maxLength={600}
                            hint='For example step-free access, a seat at the eyepiece, or a child under twelve in the group.'
                        />
                    </div>

                    <fieldset className='mt-8 space-y-3'>
                        <legend className='label-mono mb-3 text-star-3'>
                            Payment
                        </legend>
                        {cardPayments && (
                            <Choice name='night-payment' value='card' checked={payment === 'card'} onChange={() => setPayment('card')} title='Pay now by card' description='Refunded in full if we cancel for cloud.' />
                        )}
                        <Choice name='night-payment' value='on_arrival' checked={payment === 'on_arrival'} onChange={() => setPayment('on_arrival')} title='Pay on the night' description='Card or cash at the desk when you arrive.' />
                    </fieldset>

                    {message && (
                        <div className='mt-6'>
                            <Notice tone='error'>
                                {message}
                            </Notice>
                        </div>
                    )}

                    <div className='mt-8 flex items-center justify-between gap-6 border-t border-hair pt-6'>
                        <div>
                            <p className='label-mono text-star-3'>
                                Total
                            </p>
                            <p className='font-heading text-4xl text-star'>
                                {formatPrice(total)}
                            </p>
                        </div>
                        <button type='submit' disabled={sending || night.remaining === 0} className={buttonClass('primary')}>
                            {sending ? 'Reserving' : payment === 'card' ? 'Continue to payment' : 'Reserve places'}
                        </button>
                    </div>
                </form>
            )}
        </Sheet>
    )
}
